import { SDUIComponentRenderer } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIComponentRenderer/SDUIComponentRenderer";
import {
  ISDUISectionStyleWrapperProps,
  SDUISectionStyleWrapper,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUISectionRenderer/SDUISectionStyleWrapper/SDUISectionStyleWrapper";
import {
  SDUISectionInstanceRendererProps,
  SectionLoggingEventParams,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUILayoutRenderer } from "@faire/retailer-visitor-shared/components/SDUI/LayoutRenderers/SDUILayoutRenderer";
import { SDUILayoutRendererItem } from "@faire/retailer-visitor-shared/components/SDUI/LayoutRenderers/types";
import { ComponentRenderMap } from "@faire/retailer-visitor-shared/components/SDUI/types";
import { IComponent } from "@faire/web-api--source/indigofair/data/IComponent";
import { ISection } from "@faire/web-api--source/indigofair/data/ISection";
import React from "react";

export const SDUILayoutSectionRenderer: React.FC<
  SDUISectionInstanceRendererProps
> = ({
  section,
  componentRenderers,
  sectionLoggingData,
  isFilterablePaginatedSection,
  viewport,
  UPLTprops,
}) => {
  const layoutSection = section.layout_section;

  const components = layoutSection?.components;

  if (!components || components.length === 0) {
    return null;
  }

  const layoutItems = getLayoutItems(
    components,
    componentRenderers,
    sectionLoggingData,
    isFilterablePaginatedSection ?? false
  );

  const layoutSectionVisibility = getSectionVisibility(section);

  return (
    <SDUISectionStyleWrapper
      sectionStyles={section.style}
      sectionVisibility={layoutSectionVisibility}
    >
      <SDUILayoutRenderer
        items={layoutItems}
        layout={layoutSection?.layout}
        isFilterablePaginatedSection={isFilterablePaginatedSection}
        viewport={viewport}
        numFirstRows={UPLTprops?.numFirstRows}
      />
    </SDUISectionStyleWrapper>
  );
};

const getLayoutItems = (
  components: IComponent[],
  componentRenderers: ComponentRenderMap,
  sectionLoggingData: SectionLoggingEventParams,
  isFilterablePaginatedSection: boolean
): Array<SDUILayoutRendererItem> => {
  return components.map<SDUILayoutRendererItem>((component, idx) => {
    const itemKey = (component.id ?? "") + idx;
    const isPrimaryContent =
      component.is_required_for_interactivity === true &&
      !isFilterablePaginatedSection;
    return {
      item: (
        <SDUIComponentRenderer
          key={itemKey}
          component={component}
          componentRenderers={componentRenderers}
          sectionLoggingParams={sectionLoggingData}
          isPrimaryContent={isPrimaryContent}
        />
      ),
      itemKey,
      itemVisibility: component.viewports,
    };
  });
};

const getSectionVisibility = (
  section: ISection
): ISDUISectionStyleWrapperProps["sectionVisibility"] => {
  const componentWidths =
    section.layout_section?.layout?.flow_layout?.component_widths;

  if (!componentWidths) {
    return undefined;
  }

  return {
    mobileAndAbove: componentWidths.mobile_and_above.length > 0,
    tabletAndAbove: componentWidths.tablet_and_above.length > 0,
    desktopAndAbove: componentWidths.desktop_and_above.length > 0,
  };
};
